import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Eye, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";

export function ActivationEmailDialog({ api, pulse, open, onClose }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const emails = pulse?.meta?.selected || [];
  const ids = pulse?.meta?.selected_ids || [];

  const loadPreview = async (id) => {
    setBusy(`p-${id}`);
    try {
      const r = await api.get(`/admin/vcb/module/activation-email/preview?user_id=${id}`, { responseType: "text" });
      setPreview({ id, html: r.data });
    } catch { toast.error("Preview failed"); }
    finally { setBusy(null); }
  };

  const resend = async (id, email) => {
    setBusy(`r-${id}`);
    try {
      const r = await api.post("/admin/vcb/module/activation-email/resend", { user_id: id });
      toast.success(`Activation email ${r.data.status} · ${email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setPreview(null); onClose(); } }}>
      <DialogContent data-testid="vcb-activation-email-dialog" className="bg-[#050505] border border-[#D8B23E]/60 text-[#F5F5F0] max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <Mail size={16} className="text-[#D8B23E]" /> Activation Email
          </DialogTitle>
          <DialogDescription className="text-[12px] font-body text-[#C9C9C2]">
            "Your Vinci Core Banking access has been activated. Log in to explore." — preview or resend per customer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {emails.length === 0 && <div className="text-[11px] text-[#8A8A82] italic">No customers on this pulse.</div>}
          {emails.map((email, i) => {
            const id = ids[i];
            return (
              <div key={email} className="flex items-center justify-between gap-3 border border-white/10 rounded-sm px-3 py-2" data-testid={`vcb-email-row-${id}`}>
                <span className="font-body text-sm truncate">{email}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" disabled={!id || !!busy} onClick={() => loadPreview(id)} data-testid={`vcb-email-preview-${id}`}
                          className="border-white/20 text-[#C9C9C2] font-grotesk uppercase tracking-[0.2em] text-[9px]">
                    {busy === `p-${id}` ? <Loader2 size={10} className="animate-spin" /> : <><Eye size={10} className="mr-1" /> Preview</>}
                  </Button>
                  <Button size="sm" disabled={!id || !!busy} onClick={() => resend(id, email)} data-testid={`vcb-email-resend-${id}`}
                          className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.2em] text-[9px]">
                    {busy === `r-${id}` ? <Loader2 size={10} className="animate-spin" /> : <><Send size={10} className="mr-1" /> Resend</>}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        {preview && (
          <iframe title="Activation email preview" srcDoc={preview.html} sandbox=""
                  data-testid="vcb-email-preview-frame"
                  className="w-full h-[420px] bg-white rounded-sm border border-[#D8B23E]/40" />
        )}
      </DialogContent>
    </Dialog>
  );
}
